import {useGrpcRequest} from '@aboutyou/data-fetcher';
import {AutomaticSizeFilter} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/category_page/v1/stream/automatic_size_filter.types';
import {CategoryStreamService_GetProductStreamV2Lazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/category_page/v1/stream/service.grpc.lazy';
import {useSrtParam} from '@aboutyou/router';
import {
  useBasketTokenAwaitedAnySession,
  useNavigationBackOptimizedBasketToken,
} from '@aboutyou/user';
import {useAnyFilterFlyoutOpened} from '../../../features/Filters/shared/useAnyFilterFlyoutOpened';
import {
  getPagination,
  labelToSortingOption,
} from '../../../features/ProductStream/helpers';
import {
  useAppliedLiveAtFilter,
  useAppliedPotentialFilters,
} from '../../../features/ProductStream/utils';
import {
  useKeepPreviousData,
  useResetOnFetchEnd,
} from '../../../features/ProductStream/utils/useKeepPreviousData';
import {useSorting} from '../../../features/ProductStream/utils/useSorting';
import {useReductionsStateRequestParameter} from '../../../features/ReductionsState/useReductionsStateRequestParameter';
import {useFirstProductIds} from '../../../features/firstProductId/useFirstProductIds';
import {useProtoProductImageType} from '../../../shared-deprecated/useProductImageType';
import {useAppliedQuickFilter} from './useAppliedQuickFilter';
import {
  useCategoryCacheKeyTransformer,
  useCategoryIdentifier,
} from './useCategoryIdentifier';

export const useCategoryProductStream = () => {
  const appliedFilters = useAppliedPotentialFilters();
  const appliedQuickFilter = useAppliedQuickFilter();
  const liveAtFilter = useAppliedLiveAtFilter();
  const {sorting} = useSorting();
  const firstProductIds = useFirstProductIds();
  const categoryIdentifier = useCategoryIdentifier();
  const categoryCacheKeyTransformer = useCategoryCacheKeyTransformer();
  const {basketToken} = useNavigationBackOptimizedBasketToken({
    uniqueKey: 'category_stream',
  });
  const {session, refetchesDisabled} = useBasketTokenAwaitedAnySession();
  const isFilterFlyoutOpen = useAnyFilterFlyoutOpened();
  const {keepPreviousData, resetOnFetchEnd} = useKeepPreviousData();
  const preferredImage = useProtoProductImageType();
  const sortingChannel = useSrtParam();

  const request = useGrpcRequest(
    CategoryStreamService_GetProductStreamV2Lazy,
    {
      category: {
        identifier: categoryIdentifier,
      },
      pagination: getPagination(1),
      appliedFilters: {
        filters: appliedFilters,
        quickFilter: appliedQuickFilter,
        liveAt: liveAtFilter
          ? {
              minDate: liveAtFilter,
            }
          : undefined,
      },
      session,
      sortOptions: labelToSortingOption(sorting),
      firstProductIds,
      // The below values are unintended but are provided as defaults to make the request work. Otherwise the runtime throws an error.
      automaticSizeFilter: AutomaticSizeFilter.UNSPECIFIED,
      showSizeFinderBadges: false,
      showSizeFinderProfileCompletionHints: false,
      reductionsState: useReductionsStateRequestParameter(),
      basketToken,
      preferredProductImageType: preferredImage,
      enableFitmatch: false,
      fitmatchScanningCapableDevice: false,
      enableAiuta: false,
      sortingChannel,
    },
    {
      retry: 1,
      disabled: isFilterFlyoutOpen || refetchesDisabled,
      transformCacheKey: categoryCacheKeyTransformer,
      keepPreviousData,
    },
  );

  useResetOnFetchEnd(request, resetOnFetchEnd);

  return request;
};
